import { INTERNAL_NETWORK_TUNNEL_MAX_FRAME_PAYLOAD_BYTES } from './internal-network-tunnel.constants';
import {
  InternalNetworkTunnelErrorPayloadSchema,
  InternalNetworkTunnelOpenPayloadSchema,
  type InternalNetworkTunnelOpenPayload,
} from './internal-network-tunnel.schemas';

export const INTERNAL_NETWORK_TUNNEL_FRAME_TYPE = {
  OPEN: 1,
  OPENED: 2,
  DATA: 3,
  CLOSE: 4,
  ERROR: 5,
} as const;

export type InternalNetworkTunnelFrameType =
  (typeof INTERNAL_NETWORK_TUNNEL_FRAME_TYPE)[keyof typeof INTERNAL_NETWORK_TUNNEL_FRAME_TYPE];

export type InternalNetworkTunnelFrame = {
  streamId: number;
  type: InternalNetworkTunnelFrameType;
  payload: Uint8Array;
};

const HEADER_LENGTH = 9;
const EMPTY_PAYLOAD = new Uint8Array(0);

export const encodeInternalNetworkTunnelFrame = ({
  streamId,
  type,
  payload = EMPTY_PAYLOAD,
}: {
  streamId: number;
  type: InternalNetworkTunnelFrameType;
  payload?: Uint8Array;
}): Uint8Array => {
  if (!Number.isInteger(streamId) || streamId < 0 || streamId > 0xffffffff) {
    throw new Error('Invalid internal-network-tunnel stream id');
  }

  if (payload.length > INTERNAL_NETWORK_TUNNEL_MAX_FRAME_PAYLOAD_BYTES) {
    throw new Error(
      'Internal network tunnel frame exceeds the maximum payload size',
    );
  }

  const header = new Uint8Array(HEADER_LENGTH);
  const view = new DataView(header.buffer);

  view.setUint32(0, streamId);
  header[4] = type;
  view.setUint32(5, payload.length);

  return concatBytes(header, payload);
};

export const decodeInternalNetworkTunnelFrames = ({
  buffer,
}: {
  buffer: Uint8Array;
}): { frames: InternalNetworkTunnelFrame[]; remainder: Uint8Array } => {
  const frames: InternalNetworkTunnelFrame[] = [];
  const view = new DataView(
    buffer.buffer,
    buffer.byteOffset,
    buffer.byteLength,
  );
  let offset = 0;

  if (
    buffer.length >
    INTERNAL_NETWORK_TUNNEL_MAX_FRAME_PAYLOAD_BYTES + HEADER_LENGTH
  ) {
    throw new Error(
      'Internal network tunnel frame buffer exceeds the maximum size',
    );
  }

  while (buffer.length - offset >= HEADER_LENGTH) {
    const payloadLength = view.getUint32(offset + 5);

    if (payloadLength > INTERNAL_NETWORK_TUNNEL_MAX_FRAME_PAYLOAD_BYTES) {
      throw new Error(
        'Internal network tunnel frame exceeds the maximum payload size',
      );
    }

    const frameLength = HEADER_LENGTH + payloadLength;

    if (buffer.length - offset < frameLength) {
      break;
    }

    const type = buffer[offset + 4];

    if (
      type !== INTERNAL_NETWORK_TUNNEL_FRAME_TYPE.OPEN &&
      type !== INTERNAL_NETWORK_TUNNEL_FRAME_TYPE.OPENED &&
      type !== INTERNAL_NETWORK_TUNNEL_FRAME_TYPE.DATA &&
      type !== INTERNAL_NETWORK_TUNNEL_FRAME_TYPE.CLOSE &&
      type !== INTERNAL_NETWORK_TUNNEL_FRAME_TYPE.ERROR
    ) {
      throw new Error(`Unknown internal-network-tunnel frame type ${type}`);
    }

    frames.push({
      streamId: view.getUint32(offset),
      type,
      payload: buffer.subarray(offset + HEADER_LENGTH, offset + frameLength),
    });
    offset += frameLength;
  }

  return { frames, remainder: buffer.subarray(offset) };
};

export const encodeOpenPayload = (
  payload: InternalNetworkTunnelOpenPayload,
): Uint8Array => encodeUtf8(JSON.stringify(payload));

export const decodeOpenPayload = (
  payload: Uint8Array,
): InternalNetworkTunnelOpenPayload =>
  InternalNetworkTunnelOpenPayloadSchema.parse(JSON.parse(decodeUtf8(payload)));

export const encodeErrorPayload = ({
  message,
}: {
  message: string;
}): Uint8Array => encodeUtf8(JSON.stringify({ message }));

export const decodeErrorPayload = (payload: Uint8Array): string =>
  InternalNetworkTunnelErrorPayloadSchema.parse(JSON.parse(decodeUtf8(payload)))
    .message;

const concatBytes = (left: Uint8Array, right: Uint8Array): Uint8Array => {
  const result = new Uint8Array(left.length + right.length);

  result.set(left, 0);
  result.set(right, left.length);

  return result;
};

const encodeUtf8 = (value: string): Uint8Array => {
  const escaped = encodeURIComponent(value);
  const bytes: number[] = [];

  for (let index = 0; index < escaped.length; index += 1) {
    if (escaped[index] === '%') {
      bytes.push(Number.parseInt(escaped.slice(index + 1, index + 3), 16));
      index += 2;
    } else {
      bytes.push(escaped.charCodeAt(index));
    }
  }

  return Uint8Array.from(bytes);
};

const decodeUtf8 = (bytes: Uint8Array): string =>
  decodeURIComponent(
    Array.from(bytes, (byte) => `%${byte.toString(16).padStart(2, '0')}`).join(
      '',
    ),
  );
